/**
 * 메뉴 유틸리티 함수
 * Menu Utility Functions
 */

import type { SidebarMenuItem } from '@/types/sidebar-menu.types'

/**
 * 검색어로 메뉴 아이템을 재귀적으로 필터링
 * Recursively filters menu items by search query
 * 
 * @param items - 필터링할 메뉴 아이템 배열
 * @param query - 검색어
 * @returns 필터링된 메뉴 아이템 배열
 */
export function filterMenuItems(
  items: SidebarMenuItem[],
  query: string
): SidebarMenuItem[] {
  if (!Array.isArray(items) || items.length === 0) {
    return []
  }

  if (!query || query.trim() === '') {
    return items
  }

  const normalizedQuery = query.toLowerCase().trim()
  const result: SidebarMenuItem[] = []

  for (const item of items) {
    const matchesLabel = item.label?.toLowerCase().includes(normalizedQuery) ?? false

    // 하위 메뉴 검색
    let filteredChildren: SidebarMenuItem[] | undefined = undefined
    if (item.children && Array.isArray(item.children) && item.children.length > 0) {
      filteredChildren = filterMenuItems(item.children, query)
    }

    // 현재 메뉴나 하위 메뉴 중 하나라도 매칭되면 포함
    if (matchesLabel || (Array.isArray(filteredChildren) && filteredChildren.length > 0)) {
      result.push({
        ...item,
        children: filteredChildren,
      })
    }
  }

  return result
}

/**
 * 검색 결과에서 매칭된 하위 메뉴가 있는 부모 메뉴의 키를 추출
 * Extracts keys of parent menus that have matching children in search results
 * 
 * @param items - 검색 결과 메뉴 아이템 배열
 * @returns 확장해야 할 메뉴 키 Set
 */
export function getExpandedKeysForSearch(
  items: SidebarMenuItem[]
): Set<number | string> {
  const keysToExpand = new Set<number | string>()

  const traverse = (menuItems: SidebarMenuItem[]): void => {
    if (!Array.isArray(menuItems)) {
      return
    }

    for (const item of menuItems) {
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        const key = item.menuKey || item.label
        keysToExpand.add(key)
        traverse(item.children)
      }
    }
  }

  traverse(items)
  return keysToExpand
}

/**
 * 메뉴 아이템 배열을 평탄화하여 모든 아이템을 추출
 * Flattens menu items array to extract all items
 * 
 * @param items - 메뉴 아이템 배열
 * @returns 평탄화된 메뉴 아이템 배열
 */
export function flattenMenuItems(items: SidebarMenuItem[]): SidebarMenuItem[] {
  const result: SidebarMenuItem[] = []

  const traverse = (menuItems: SidebarMenuItem[]): void => {
    if (!Array.isArray(menuItems)) {
      return
    }

    for (const item of menuItems) {
      result.push(item)
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        traverse(item.children)
      }
    }
  }

  traverse(items)
  return result
}

/**
 * menuKey로 메뉴 아이템 찾기
 * Find menu item by menuKey
 * 
 * @param items - 메뉴 아이템 배열
 * @param menuKey - 찾을 메뉴 키
 * @returns 찾은 메뉴 아이템 또는 undefined
 */
export function findMenuItemByKey(
  items: SidebarMenuItem[],
  menuKey: number | string
): SidebarMenuItem | undefined {
  const flatItems = flattenMenuItems(items)
  return flatItems.find(item => item.menuKey === menuKey)
}
